import { Button, Dropdown, Menu, Typography } from 'antd';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { exportFile } from '../header-actions';
import { useLocation } from 'react-router-dom';
import styles from '../../navbar/navbarMenu.module.scss';
import { getExportLoading, getImportLoading } from '../header-reducer';
import {
  getReadTabsPermissions,
  getWriteTabsPermissions,
  getUserPositionsLoading,
} from '../../../store/user/user-reducer';
import { getPermittedFeatures } from '../../../store/user/user-reducer';
import { ExportImportButton, ExportImportButtonText } from '@med-fe/ui';
import { openTabsExport } from '../../export/export-actions';

function Export({ isTemplateAccessible = true }) {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isLoading = useSelector(getExportLoading);
  const isImportLoading = useSelector(getImportLoading);
  const isReadTabsAllowed = useSelector(getReadTabsPermissions);
  const isWriteTabsAllowed = useSelector(getWriteTabsPermissions);
  const isNoTabsReadAllowed = Object.values(isReadTabsAllowed ?? {}).every((tab) => !tab);
  const isNoTabsWriteAllowed = Object.values(isWriteTabsAllowed ?? {}).every((tab) => !tab);
  const isDisabled = isLoading || isImportLoading || (isNoTabsReadAllowed && isNoTabsWriteAllowed);
  const [current, setCurrent] = useState('0');
  const permittedFeaturesRaw = useSelector(getPermittedFeatures) || [];

  const normalizeFeature = (f: any) => {
    if (!f && f !== 0) return '';
    if (typeof f === 'string') return f;
    if (typeof f === 'number') return String(f);
    return String(f.Name || f.name || f.DisplayName || f.displayName || f.Feature || f.feature || f.Key || f.key || '');
  };

  const permittedFeatures = (permittedFeaturesRaw || []).map(normalizeFeature).filter(Boolean);

  // flexible matching: support variations like 'Export_CSV', 'EXPORT.CSV', 'Export CSV', 'ExportExcel', 'xlsx'
  const csvRegex = /export[^a-z0-9]*csv|csv[^a-z0-9]*export|\bcsv\b/i;
  const excelRegex = /export[^a-z0-9]*excel|excel[^a-z0-9]*export|\bxlsx\b|\bexcel\b/i;
  const importTemplateRegex = /export[^a-z0-9]*csv|csv[^a-z0-9]*import|\btemplate\b/i;

  const hasExportCsv = permittedFeatures.some((f: any) => csvRegex.test(String(f)));
  const hasExportExcel = permittedFeatures.some((f: any) => excelRegex.test(String(f)));
  const hasImportTemplate = permittedFeatures.some((f: any) => importTemplateRegex.test(String(f)));

  // debug: print permitted features and computed flags (remove after verification)
  // eslint-disable-next-line no-console
  console.debug('Export control debug =>', {
    pathname,
    permittedFeaturesRaw,
    permittedFeatures,
    hasExportCsv,
    hasExportExcel,
    hasImportTemplate,
  });

  // Determine which export options are actually visible for the current route
  const userPositionsLoading = useSelector(getUserPositionsLoading);
  const canShowCsv = !pathname.includes('vendor') && hasExportCsv;
  const canShowExcel = hasExportExcel;
  const canShowTemplate = pathname.includes('offices') && isTemplateAccessible && hasImportTemplate;
  const hasVisibleExportOption = canShowCsv || canShowExcel || canShowTemplate;

  // Special-case: on vendor pages CSV export isn't supported — if user only has CSV (no Excel), hide control.
  if (pathname.includes('vendor') && !userPositionsLoading && !hasExportExcel) return null;

  // If user has no visible export options for this route, hide the export control
  // but don't hide while user positions/permissions are still loading.
  if (!userPositionsLoading && !hasVisibleExportOption) return null;

  const handleItem = (type) => {
    if (!isLoading) {
      const path = pathname.includes('offices')
        ? 'locations'
        : pathname.includes('doctors')
        ? 'employees'
        : pathname.includes('vendor')
        ? 'vendors'
        : 'audit';
      let payload: any = { path, type };

      if (type.includes('template')) {
        payload = { ...payload, isTemplate: true };
      }

      dispatch(exportFile(payload));
    }
  };

  const menu = (
    <Menu
      onClick={(e) => {
        setCurrent(e.key);
      }}
      selectedKeys={[current]}
      className={styles['menu']}
    >
      {!pathname.includes('vendor') && hasExportCsv && (
        <Menu.Item className={styles['menu-item']} key='csv' onClick={(e) => handleItem('csv')}>
          <Typography className={styles['link']}>CSV</Typography>
        </Menu.Item>
      )}

      {hasExportExcel && (
        <Menu.Item key='xlsx' className={styles['menu-item']} onClick={(e) => handleItem('xlsx')}>
          <Typography className={styles['link']}>Excel</Typography>
        </Menu.Item>
      )}
      {pathname.includes('offices') && isTemplateAccessible && hasImportTemplate && (
        <Menu.Item key='template' className={styles['menu-item']} onClick={(e) => dispatch(openTabsExport())}>
          <Typography className={styles['link']}>Import Template</Typography>
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={isLoading ? <div /> : menu} disabled={isDisabled}>
      <ExportImportButton type={'link'} loading={isLoading} icon={<img alt='export' src={'./images/export.svg'} />}>
        <ExportImportButtonText>{isLoading ? 'Exporting' : 'Export'}</ExportImportButtonText>
      </ExportImportButton>
    </Dropdown>
  );
}

export default Export;
